import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AppService } from './app.service';
import { ConfigService } from '@neworg-net/amsuite-core/config';
import { requireHostUrl } from '../shared/required-host-url';
import { httpOptions } from '../constants/http-options.constant';
import {
  adaptWorkspaceDashboard, adaptWorkspaceDashboards, toCreateRequest, toUpdateRequest
} from './workspace-dashboard.adapter';
import { WorkspaceDashboardDto, LegacyDashboard } from '../models';

@Injectable({ providedIn: 'root' })
export class DashboardService {
  private _http = inject(HttpClient);
  private _appService = inject(AppService);
  private readonly config = inject(ConfigService);

  // Legacy (monolite ADApi) — restano invariati
  private amSuiteUrl = this._appService.getServerBaseUrl + '/amsuite';
  public serviceUrl = this._appService.getServerBaseUrl + '/auth';
  public partyUrl = this._appService.getServerBaseUrl + '/party';
  public dialogUrl = this._appService.getServerBaseUrl + '/dialog';

  // Nuovo: servizio Workspace REST.
  // L'header X-Party-Owner-Id è iniettato centralmente da PartyOwnerInterceptor
  // (vale per tutte le chiamate authn + workspace), che attende getDataOwner se serve.
  private workspaceUrl = requireHostUrl(this.config, 'workspace') + '/v1/dashboards';

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);
  constructor() {}

  // ---------- LEGACY (invariati) ----------
  getAppWidgets(appId: any): Observable<any> {
    const url = `${this.amSuiteUrl}/dashboardwidgets?appId=${appId}`;
    return this._http.get(url, httpOptions);
  }

  getDialogRequestTypologyes(): Observable<any> {
    const url = `${this.dialogUrl}/types`;
    return this._http.get(url, httpOptions);
  }

  // ---------- MIGRATI a /v1/dashboards ----------
  getUserDashboards(): Observable<LegacyDashboard[]> {
    const url = `${this.workspaceUrl}?appIdQuery=${this.config.appId}`;
    return this._http.get<WorkspaceDashboardDto[]>(url, httpOptions).pipe(map(adaptWorkspaceDashboards));
  }

  getUserDashboard(idDashboard: any): Observable<LegacyDashboard> {
    const url = `${this.workspaceUrl}/${idDashboard}`;
    return this._http.get<WorkspaceDashboardDto>(url, httpOptions).pipe(map(adaptWorkspaceDashboard));
  }

  createDashboard(appId: any): Observable<LegacyDashboard> {
    const body = toCreateRequest(appId, 'Principale', []);
    return this._http.post<WorkspaceDashboardDto>(this.workspaceUrl, body, httpOptions).pipe(map(adaptWorkspaceDashboard));
  }

  /**
   * "Salva come" personale (feature B): crea una NUOVA dashboard utente con
   * nome esplicito e i widget legacy correnti. Distinto da createDashboard
   * (vuota) e da duplicateDashboard (forza il suffisso "(Copia)").
   */
  createDashboardWithWidgets(appId: any, name: string, legacyWidgets: any[]): Observable<LegacyDashboard> {
    const body = toCreateRequest(appId ?? this.config.appId, name, legacyWidgets);
    return this._http.post<WorkspaceDashboardDto>(this.workspaceUrl, body, httpOptions).pipe(map(adaptWorkspaceDashboard));
  }

  duplicateDashboard(dashboard: any): Observable<LegacyDashboard> {
    const body = toCreateRequest(dashboard.appId ?? this.config.appId, `${dashboard.name} (Copia)`, dashboard.widgets);
    return this._http.post<WorkspaceDashboardDto>(this.workspaceUrl, body, httpOptions).pipe(map(adaptWorkspaceDashboard));
  }

  saveDashboard(dashboard: any): Observable<LegacyDashboard> {
    const url = `${this.workspaceUrl}/${dashboard.id}`;
    const body = toUpdateRequest(dashboard);
    return this._http.put<WorkspaceDashboardDto>(url, body, httpOptions).pipe(map(adaptWorkspaceDashboard));
  }

  removeDashboard(dashboardId: any): Observable<void> {
    const url = `${this.workspaceUrl}/${dashboardId}`;
    return this._http.delete<void>(url, httpOptions);
  }

  defaultDashboard(dashboardId: any): Observable<void> {
    const url = `${this.workspaceUrl}/${dashboardId}/default`;
    return this._http.post<void>(url, null, httpOptions);
  }
}
